/** Currency formatting and arithmetic utilities for payment processing. Browser-compatible. */
import { DEFAULT_LOCALE, DEFAULT_CURRENCY_ISO } from '@federa/const/assistant';
import { CURRENCY_POSITIVE_SIGN, formatUsdPlain, formatUsdPrecise } from '@federa/const/currency-format';
import { getOrCreateMapEntry } from '@federa/const/map-get-or-create';
import type { Nullish } from '@federa/types/primitives-records';

/** Whole-dollar USD currency formatter (no fractional digits). */
const USD_FORMATTER = new Intl.NumberFormat(DEFAULT_LOCALE, {
	style: 'currency',
	currency: DEFAULT_CURRENCY_ISO,
	maximumFractionDigits: 0,
});

/** The to-the-cent formatter of each currency a caller has named, built once per code: `Intl` construction is the cost. */
const PRECISE_FORMATTERS = new Map<string, Intl.NumberFormat>();

function preciseFormatterOf(currency: string): Intl.NumberFormat {
	return getOrCreateMapEntry(
		PRECISE_FORMATTERS,
		currency,
		() => new Intl.NumberFormat(DEFAULT_LOCALE, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }),
	);
}

/** Format a scalar amount with the given `Intl.NumberFormat`, coercing nullish / NaN input to 0 (or `'-'` when `showZeroAsDash` is set and the value is zero or invalid). */
function formatWithFormatter(amount: Nullish, formatter: Intl.NumberFormat, options: { showZeroAsDash?: boolean }): string {
	const numValue = amount == null ? 0 : Number(amount);
	const isInvalid = Number.isNaN(numValue);
	if (options.showZeroAsDash && (isInvalid || numValue === 0)) return '-';
	return formatter.format(isInvalid ? 0 : numValue);
}

// `centsToDollars` / `dollarsToCents` moved to `const/money-conversion.ts` on 2026-08-14: they convert NUMBERS and
// have no `Intl` dependency, so their home beside these formatters put them behind the utils floor -- unreachable
// from any other `utils/` leaf. This file keeps the STRING renderers.

export function formatCurrency(amount: Nullish, options = {}): string {
	return formatWithFormatter(amount, USD_FORMATTER, options);
}
export function formatCurrencyPrecise(amount: Nullish, options: { showZeroAsDash?: boolean } = {}): string {
	return formatCurrencyPreciseIn(amount, DEFAULT_CURRENCY_ISO, options);
}

/**
 * A money figure to the cent in the currency its row names -- `$1.00` for USD, a euro sign for EUR -- so a book that
 * holds more than one currency never prints a euro as a dollar. The two dollar renderers beside it are this at the
 * default currency. `showZeroAsDash` as {@link formatCurrencyPrecise}; `signed` as {@link formatCurrencySigned}.
 */
export function formatCurrencyPreciseIn(amount: Nullish, currency: string, options: { showZeroAsDash?: boolean; signed?: boolean } = {}): string {
	const sign = options.signed === true && Number(amount) > 0 ? CURRENCY_POSITIVE_SIGN : '';
	return `${sign}${formatWithFormatter(amount, preciseFormatterOf(currency), options)}`;
}

/**
 * A SIGNED money figure, to the cent: `+$100.00` for money in, `-$25.00` for money out, `$0.00` for neither. The ONE
 * spelling of that decision -- a wallet row (`components/modals/PaymentsModal.components.tsx`) and a market maker's net
 * (`spa/market-maker/frontend/mm/page.detail-mm-ledger.tsx`) each composed it by hand. `Intl` already prints a
 * negative's minus outside the symbol, so only the positive's plus is spelled ({@link formatCurrencyPreciseIn}).
 */
export function formatCurrencySigned(amount: number): string {
	return formatCurrencyPreciseIn(amount, DEFAULT_CURRENCY_ISO, { signed: true });
}

/** Formats a dollar amount as `$<num>` with DEFAULT_LOCALE grouping, delegating to the `@federa/const/currency-format` SSOT so this leaf and the pure `utils/title` / `utils/market-maker` money renderers cannot disagree on grouping. `{ precise: true }` gives 2 decimals, the default none. Amounts are whole dollars -- cents callers pass `centsToDollars(x)`. For a locale-aware currency symbol use `formatCurrency`. */
export function formatUsd(amount: number, options: { precise?: boolean } = {}): string {
	return options.precise ? formatUsdPrecise(amount) : formatUsdPlain(amount);
}
/** Formats a dollar amount as `$<num>`, falling back to a dash sentinel (default `'--'`, override via `fallback`) when the amount is nullish, non-finite, or non-positive. */
export function formatUsdOrDash(amount: number | null | undefined, options: { precise?: boolean; fallback?: string } = {}): string {
	if (amount == null || !Number.isFinite(amount) || amount <= 0) return options.fallback ?? '--';
	return formatUsd(amount, { precise: options.precise });
}
/** Format a bid/offer amount for notification copy, falling back to a generic phrase when the amount is non-positive or non-finite. Thin delegation to `formatUsdOrDash`. */
export function formatBidAmount(amount: number, fallback: string): string {
	return formatUsdOrDash(amount, { fallback });
}
/** Format a price as currency, returning "-" for nullish values. */
export function formatPriceDash(value: Nullish): string {
	if (value == null) return '-';
	return formatCurrency(value);
}
/** Strip non-digit characters and return the raw number (or empty string). */
export function parseCurrencyInput(raw: string): number | '' {
	const digits = raw.replace(/\D/g, '');
	return digits === '' ? '' : Number(digits);
}
/** Format a stored numeric value for display (e.g. 500000 -> "$500,000"). */
export function displayCurrency(value: unknown): string {
	if (value === '' || value === null || value === undefined) return '';
	const num = Number(value);
	return Number.isNaN(num) ? '' : formatCurrency(num);
}
